import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import {
    Collapse,
    Table,
    Tabs,
    Tag,
    Typography,
    Button,
    Popconfirm,
    message,
    Modal as AntModal,
    Tooltip
} from "antd";
import { DeleteOutlined, UnlockOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { GetJutgeProblemesIdEstadistiques } from "../../../../dao/jutge/GetJutgeProblemesIdEstadistiques";
import { DeleteJutgeEnviament } from "../../../../dao/jutge/DeleteJutgeEnviament";
import JutgeColors from "../../../../styles/JutgeColors";
import { getJavamonImage } from "../../../../dao/jutge/GetJavamonConfig";
import Editor from "@monaco-editor/react";
import ModoDesafioTag from "../../activitatsId/content/ModoDesafioTag";

const { Panel } = Collapse;
const { Text } = Typography;

interface TestResult {
    ordre: number;
    estat: number;
    expected: string;
    actual: string;
    errors: string;
}

interface FileData {
    uid: string;
    nom: string;
    fitxer: string; // Base64
}

interface JunitData {
    id: number;
    tipus: string;
    ordre: number;
    fitxer: string;
    moment: string;
    name: string;
}

interface EnviamentData {
    id_enviament: number;
    moment: string;
    files: string; // JSON string
    tests: string; // JSON string
    junit: string; // JSON string
    // Parsed data
    parsedFiles?: FileData[];
    parsedTests?: TestResult[];
    parsedJunit?: JunitData[];
}

interface ProblemaIdEstadistiquesProps {
    userId?: string;
    idProblem?: string;
    idActivity?: string;
    isConcurs?: boolean;
    refreshTrigger?: number;
}

const ProblemaIdEstadistiques: React.FC<ProblemaIdEstadistiquesProps> = ({ userId: propUserId, idProblem: propIdProblem, idActivity: propIdActivity, isConcurs, refreshTrigger }) => {
    const { isLoggedIn, userId: contextUserId, user } = useContext(AuthContext);
    const userId = propUserId || contextUserId;
    const { id_problema: paramIdProblema, id_activitat: paramIdActivitat } = useParams();

    const id_problema = propIdProblem || paramIdProblema || "0";
    const id_activitat = propIdActivity || paramIdActivitat || "0";

    const [dades, setDades] = useState<EnviamentData[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeKeys, setActiveKeys] = useState<string | string[]>([]);

    const [unlockedHints, setUnlockedHints] = useState<Record<number, { image: string, id_instance: number }>>({}); // ordre -> hint data
    const [availableHintJavamons, setAvailableHintJavamons] = useState<any[]>([]);
    const [hintModalVisible, setHintModalVisible] = useState(false);
    const [selectedOrdre, setSelectedOrdre] = useState<number | null>(null);

    // Use URL from environment
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (isLoggedIn) {
            checkDades();
        }
    }, [id_problema, id_activitat, refreshTrigger]);

    useEffect(() => {
        if (userId && id_problema !== "0" && !isConcurs) {
            fetchHintInfo();
        }
    }, [userId, id_problema, isConcurs]);

    const fetchHintInfo = async () => {
        // Fetch Status (Array of unlocked hints)
        const statusRaw = await import("../../../../dao/jutge/GetHintStatus").then(m => m.GetHintStatus(userId, id_problema));

        let newUnlocked: Record<number, any> = {};

        if (Array.isArray(statusRaw)) {
            statusRaw.forEach((item: any) => {
                let parsedItem = item;
                // Handle various formats if necessary (sometimes wrappers differ)
                if (item.json) {
                    try {
                        parsedItem = JSON.parse(item.json);
                    } catch (e) {
                        console.error("Error parsing hint item json", e);
                    }
                }

                if (parsedItem && typeof parsedItem.ordre !== 'undefined') {
                    newUnlocked[parsedItem.ordre] = {
                        image: parsedItem.image,
                        imagen: parsedItem.imagen,
                        id_instance: parsedItem.id_instance
                    };
                }
            });
        }

        setUnlockedHints(newUnlocked);

        // Verify if user has javamons
        const javamonsRaw = await import("../../../../dao/jutge/GetJutgeUsuariIdJavamonsHint").then(m => m.GetJutgeUsuariIdJavamonsHint(userId, id_problema));
        if (javamonsRaw && javamonsRaw.json) {
            let arr = JSON.parse(javamonsRaw.json);
            if (Array.isArray(arr)) {
                setAvailableHintJavamons(arr);
            }
        }
    }

    const openHintModal = (ordre: number) => {
        setSelectedOrdre(ordre);
        setHintModalVisible(true);
    }

    const handleUnlock = async (javamonId: string) => {
        if (selectedOrdre === null) return;

        await import("../../../../dao/jutge/PostHint").then(m => m.PostHint(userId, id_problema, javamonId, selectedOrdre));
        setHintModalVisible(false);
        fetchHintInfo();
        message.success("Pista desbloqueada!");
        setSelectedOrdre(null);
    }

    const isAllCorrect = (env: EnviamentData) => {
        if (!env.parsedTests || env.parsedTests.length === 0) return false;
        return env.parsedTests.every(test => test.estat === 1);
    }

    async function checkDades() {
        setLoading(true);
        const rawData = await GetJutgeProblemesIdEstadistiques(id_problema + "", userId, id_activitat || "0"); // Fixed ID conversion
        console.log(rawData);

        let parsedData: EnviamentData[] = [];
        try {
            let temp = rawData;
            if (typeof rawData === 'string') {
                temp = JSON.parse(rawData);
            } else if (Array.isArray(rawData)) {
                temp = rawData;
            } else if (rawData && rawData.json) {
                temp = JSON.parse(rawData.json);
            }

            if (Array.isArray(temp)) {
                const sorted = temp
                    .filter((d: any) => d && d.moment)
                    .map((d: any) => ({
                    ...d,
                    parsedFiles: d.files
                        ? (typeof d.files === 'string' ? JSON.parse(d.files) : d.files)
                        : [],
                    parsedTests: (d.tests
                        ? (typeof d.tests === 'string' ? JSON.parse(d.tests) : d.tests)
                        : []).sort((a: any, b: any) => a.ordre - b.ordre),
                    parsedJunit: d.junit
                        ? (typeof d.junit === 'string' ? JSON.parse(d.junit) : d.junit)
                        : []
                }))
                    .sort((a: EnviamentData, b: EnviamentData) => b.id_enviament - a.id_enviament);
                setDades(sorted);

                if (sorted.length > 0) {
                    setActiveKeys([sorted[0].id_enviament.toString()]);
                }
            } else {
                setDades([]);
            }

        } catch (e) {
            console.error("Error parsing statistics:", e);
            setDades([]);
        }

        setLoading(false);
    }

    const deleteEnviament = async (id: number) => {
        try {
            const response = await DeleteJutgeEnviament(id.toString());
            if (response.ok) {
                message.success("Enviament eliminat correctament");
                checkDades();
            } else {
                message.error("Error al eliminar l'enviament");
            }
        } catch (e) {
            console.error(e);
            message.error("Error de connexió");
        }
    };

    const javamonImage = (jm: any) => {
        return getJavamonImage(jm);
    }

    // We no longer filter by image here because the backend already handles 
    // filtering out Javamons with used=1. 
    // And users SHOULD be able to use different instances of the same species.
    const usableJavamons = availableHintJavamons;

    const renderFileContent = (file: FileData) => {
        let content = "";
        try {
            content = atob(file.fitxer);
        } catch (e) {
            content = "Error decoding file content";
        }

        return (
            <Editor
                height="300px"
                defaultLanguage="java"
                value={content}
                theme="light"
                options={{
                    readOnly: true,
                    automaticLayout: true,
                    minimap: { enabled: false }
                }}
            />
        );
    };

    // Helper to render complex JUnit JSON data (Method signatures, Classes, etc.)
    const renderJunitVisual = (junitTest: JunitData | undefined, content: string) => {
        if (!junitTest) return <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;

        try {
            const tipus = junitTest.tipus;
            // Fallback for non-json or other types
            const fallback = <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;

            if (["method", "class", "content_class", "exception_check", "io_file"].includes(tipus)) {
                let data: any;
                try { data = JSON.parse(content); } catch (e) { return fallback; }

                if (tipus === "method") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Mètode:</b> {data.methodName}</p>
                            <p style={{ marginBottom: 2 }}><b>Modificadors: </b>
                                {data.modifiers && data.modifiers.length > 0 ? (
                                    data.modifiers.map((m: string, idx: number) => (
                                        <Tag key={idx} color="purple" style={{ marginRight: 2 }}>{m}</Tag>
                                    ))
                                ) : (
                                    <Tag color="red" style={{ marginRight: 2 }}>Sense modificadors</Tag>
                                )}
                            </p>
                            <p style={{ marginBottom: 2 }}><b>Retorn:</b> <Tag color={JutgeColors.colors.color3}>{data.returnType}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Paràmetres:  </b>
                                {data.parameters && data.parameters.length > 0 ? (
                                    data.parameters.map((p: any, idx: number) => (
                                        <React.Fragment key={idx}>
                                            <Tag color={JutgeColors.colors.color4} style={{ marginRight: 2 }}>{p.paramName}</Tag>
                                            {"-->"}
                                            <Tag color={JutgeColors.colors.color2} style={{ marginLeft: 2, marginRight: 2 }}>{p.paramType}</Tag>
                                            {"  ,  "}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Tag color="red" >Sense paràmetres</Tag>
                                )}
                            </p>
                        </div>
                    );
                }

                if (tipus === "class") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <div style={{ marginBottom: 8 }}>
                                {data.modifiers && data.modifiers.map((m: string, idx: number) => (
                                    <Tag key={idx} color="purple" style={{ marginRight: 2 }}>{m}</Tag>
                                ))}
                                <Tag color="magenta" style={{ marginRight: 2 }}>{data.classType}</Tag>
                                <span style={{ fontWeight: 'bold', marginRight: 8, color: '#1677ff' }}>{data.className}</span>

                                {data.extendedClass && (
                                    <span>
                                        <span style={{ color: '#999', marginRight: 4 }}>extends</span>
                                        <Tag color="orange" style={{ marginRight: 2 }}>{data.extendedClass}</Tag>
                                    </span>
                                )}

                                {data.implementedInterfaces && data.implementedInterfaces.length > 0 && (
                                    <span>
                                        <span style={{ color: '#999', margin: '0 4px' }}>implements</span>
                                        {data.implementedInterfaces.map((i: string, idx: number) => <Tag key={idx} color="cyan" style={{ marginRight: 2 }}>{i}</Tag>)}
                                    </span>
                                )}
                            </div>

                            {data.classType === 'enum' && data.enumValues && data.enumValues.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Valors Enum:</b>
                                    <div style={{ paddingLeft: 10, marginTop: 4 }}>
                                        {data.enumValues.map((val: string, idx: number) => (
                                            <Tag key={idx} color="gold" style={{ marginRight: 2 }}>{val}</Tag>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {data.constructors && data.constructors.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Constructors:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.constructors.map((c: any, idx: number) => (
                                            <li key={idx}>
                                                {c.modifiers && c.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <b>{data.className}</b>(
                                                {c.parameters && c.parameters.map((p: any, pi: number) => (
                                                    <span key={pi}>
                                                        {p.paramType} {p.paramName}{pi < c.parameters.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                                )
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.fields && data.fields.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Atributs:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.fields.map((f: any, idx: number) => (
                                            <li key={idx}>
                                                {f.modifiers && f.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <Tag color={JutgeColors.colors.color4} style={{ marginRight: 2 }}>{f.fieldName}</Tag>
                                                : <Tag color={JutgeColors.colors.color3} style={{ marginLeft: 2, marginRight: 2 }}>{f.fieldType}</Tag>
                                                {f.checkGetter && <Tag color="green" style={{ fontSize: '0.8em', marginRight: 2 }}>G</Tag>}
                                                {f.checkSetter && <Tag color="blue" style={{ fontSize: '0.8em' }}>S</Tag>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {data.methods && data.methods.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <b>Mètodes:</b>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {data.methods.map((m: any, idx: number) => (
                                            <li key={idx}>
                                                {m.modifiers && m.modifiers.map((mod: string, i: number) => <span key={i} style={{ color: '#999', fontSize: '0.9em' }}>{mod} </span>)}
                                                <Tag color={JutgeColors.colors.color3} style={{ marginRight: 2 }}>{m.returnType}</Tag>
                                                <b>{m.methodName}</b>(
                                                {m.parameters && m.parameters.map((p: any, pi: number) => (
                                                    <span key={pi}>
                                                        {p.paramType} {p.paramName}{pi < m.parameters.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))}
                                                )
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
                }

                if (tipus === "content_class") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Contingut en Clase:</b> <Tag color="geekblue">{data.refClassName || "Unknown"}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Text a buscar:</b> <Tag>{data.searchText}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Condició:</b> {data.mustContain ? <Tag color="green">Ha de contenir</Tag> : <Tag color="red">No ha de contenir</Tag>}</p>
                        </div>
                    );
                }

                if (tipus === "exception_check") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Excepció en:</b> <Tag color="geekblue">{data.refClassName || "?"}.{data.methodName}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Excepció Esperada:</b> <Tag color="red">{data.exceptionType}</Tag></p>
                            {data.expectedOutput && (
                                <p style={{ marginBottom: 2 }}><b>Sortida Esperada (Missatge):</b> <br />
                                    <Tag color="orange" style={{ whiteSpace: 'pre-wrap' }}>{data.expectedOutput}</Tag>
                                </p>
                            )}
                        </div>
                    );
                }

                if (tipus === "io_file") {
                    return (
                        <div style={{ fontSize: '0.75rem' }}>
                            <p style={{ marginBottom: 2 }}><b>Entrada per Fitxer:</b> <Tag color="blue">Input File</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Tipus Sortida:</b> <Tag>{data.outputType || "text"}</Tag></p>
                            <p style={{ marginBottom: 2 }}><b>Sortida Esperada:</b> <Tag color="orange" style={{ whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'auto', display: 'block' }}>{data.output || data.outputContent || "Sense sortida definida"}</Tag></p>
                        </div>
                    );
                }
            }

            return fallback;
        } catch (e) {
            return <pre style={{ fontSize: '0.75rem', margin: 0, whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>{content}</pre>;
        }
    }

    const getColumns = (currentEnv: EnviamentData): ColumnsType<TestResult> => [
        {
            title: 'Test',
            dataIndex: 'ordre',
            key: 'ordre',
            width: 80,
            render: (text) => <Tag color={JutgeColors.colors.color3}>#{text}</Tag>
        },
        {
            title: 'Estat',
            dataIndex: 'estat',
            key: 'estat',
            width: 100,
            render: (estat) => (
                <Tag color={estat === 1 ? JutgeColors.colors.color4 : JutgeColors.colors.color1}>
                    {estat === 1 ? "CORRECTE" : "INCORRECTE"}
                </Tag>
            )
        },
        {
            title: 'Entrada',
            key: 'entrada',
            width: 300,
            render: (_, record) => {
                const isUnlocked = unlockedHints.hasOwnProperty(record.ordre);
                const canView = (user.tipus === "1" || user.tipus === "0") || isUnlocked;

                if (!canView) return <Text disabled italic>Ocult</Text>;

                const junitTest = currentEnv.parsedJunit?.find(j => j.ordre === record.ordre);
                let junitContent = "";

                if (junitTest && junitTest.fitxer) {
                    const val = junitTest.fitxer;
                    let isJson = false;
                    try {
                        const dj = JSON.parse(val);
                        if (dj && typeof dj === 'object') isJson = true;
                    } catch (e) { }

                    if (!isJson && val.includes(":")) {
                        const parts = val.split(":");
                        if (parts.length > 2) {
                            try { junitContent = atob(parts[2]); } catch (e) { }
                        }
                    }

                    if (!junitContent && !isJson) {
                        try { junitContent = atob(val); } catch (e) { junitContent = val; }
                    } else if (!junitContent) {
                        junitContent = val;
                    }
                }

                if (!junitContent) return <Text type="secondary">-</Text>;

                return (
                    <div style={{ maxHeight: '150px', overflow: 'auto' }}>
                        {renderJunitVisual(junitTest, junitContent)}
                    </div>
                );
            }
        },
        {
            title: 'Errors / Detalls',
            key: 'details',
            render: (_, record) => {
                if (record.estat === 1) return <Text type="success">OK</Text>;

                const isUnlocked = unlockedHints.hasOwnProperty(record.ordre);
                const canView = (user.tipus === "1" || user.tipus === "0") || isUnlocked;

                if (canView) {
                    const errorContent = record.errors === "exception" ? (
                        <Tooltip
                            title={<pre style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflow: 'auto' }}>{record.actual}</pre>}
                            trigger="hover"
                            overlayStyle={{ maxWidth: '600px' }}
                        >
                            <Tag color="red" style={{ cursor: 'pointer' }}>EXCEPTION</Tag>
                        </Tooltip>
                    ) : (
                        record.errors && <pre style={{ margin: 0, color: 'red' }}>{record.errors}</pre>
                    );

                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ maxHeight: '100px', overflow: 'auto', fontSize: '0.85em' }}>
                                {isUnlocked && (
                                    <div style={{ marginBottom: 5 }}>
                                        <Tag color="gold" icon={<UnlockOutlined />}>Pista Usada</Tag>
                                        <img src={javamonImage({ image: (unlockedHints[record.ordre] as any).image, imagen: (unlockedHints[record.ordre] as any).imagen })} alt="Javamon" style={{ width: 20, height: 20, borderRadius: '50%', marginLeft: 5, objectFit: 'cover' }} />
                                    </div>
                                )}
                                {errorContent}
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Text type="secondary" italic>Detalls ocults</Text>
                            {usableJavamons.length > 0 && !isConcurs && (
                                <Tooltip title="Usa un Javamon para ver el error">
                                    <Button
                                        size="small"
                                        type="primary"
                                        ghost
                                        style={{ borderColor: 'gold', color: 'orange' }}
                                        onClick={() => openHintModal(record.ordre)}
                                    >
                                        Revelar Pista
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    )
                }
            }
        }
    ];

    return (
        <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Historial d'Enviaments</h3>
                <div>
                    {/* Hints are now per test */}
                </div>
            </div>

            {loading && <p>Carregant...</p>}

            {!loading && dades.length === 0 && <p>No hi ha enviaments.</p>}

            <Collapse
                accordion
                activeKey={activeKeys}
                onChange={(key) => setActiveKeys(key)}
            >
                {dades.map((env, index) => {
                    const allCorrect = isAllCorrect(env);
                    const bgColor = allCorrect ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)';

                    return (
                        <Panel
                            style={{ background: bgColor, marginBottom: 5, borderRadius: 4, border: 'none' }}
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Enviament #{env.id_enviament} - {env.moment} {allCorrect ? <Tag color="green">PASSED</Tag> : <Tag color="red">FAILED</Tag>}
                                        {" "}<ModoDesafioTag record={env} hideWhenPending />
                                    </span>
                                    {(user.tipus === "1" || user.tipus === "0") && (
                                        <div onClick={e => e.stopPropagation()}>
                                            <Popconfirm
                                                title="Eliminar enviament?"
                                                description="Això eliminarà l'enviament, els fitxers i els resultats."
                                                onConfirm={() => deleteEnviament(env.id_enviament)}
                                                okText="Sí"
                                                cancelText="No"
                                            >
                                                <Button type="text" danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    )}
                                </div>
                            } key={env.id_enviament}
                        >
                            <Tabs defaultActiveKey="1" items={[
                                {
                                    key: '1',
                                    label: 'Resultats Tests',
                                    children: (
                                        <Table
                                            dataSource={env.parsedTests}
                                            columns={getColumns(env)}
                                            rowKey="ordre"
                                            pagination={false}
                                            size="small"
                                        />
                                    )
                                },
                                {
                                    key: '2',
                                    label: 'Ficheros',
                                    children: (
                                        <Tabs
                                            tabPosition="left"
                                            items={env.parsedFiles?.map((f, i) => ({
                                                key: f.uid || i.toString(),
                                                label: f.nom,
                                                children: renderFileContent(f)
                                            }))}
                                        />
                                    )
                                }
                            ]} />
                        </Panel>
                    )
                })}
            </Collapse>

            <AntModal
                title={`Selecciona un Javamon para revelar la pista #${selectedOrdre}`}
                open={hintModalVisible}
                onCancel={() => setHintModalVisible(false)}
                footer={null}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10 }}>
                    {usableJavamons.map(javamon => (
                        <div key={javamon.id} onClick={() => handleUnlock(javamon.id)} style={{ cursor: 'pointer', textAlign: 'center', border: '1px solid #ddd', padding: 5, borderRadius: 8 }}>
                            <img src={javamonImage({image: javamon.image, imagen: javamon.imagen})} alt={javamon.nom} style={{ width: 60, height: 60, objectFit: 'contain' }} />
                            <div style={{ fontSize: 12, fontWeight: 'bold' }}>{javamon.nom}</div>
                        </div>
                    ))}
                    {usableJavamons.length === 0 && <p>No tens Javamons disponibles per revelar pistes.</p>}
                </div>
            </AntModal>
        </div>
    );
};

export default ProblemaIdEstadistiques;
